import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CalendarDays } from "lucide-react";

const BOOKING_URL = "https://api.leadconnectorhq.com/widget/booking/qradOZhUywW2EZ7FBIbw";
const DEFAULT_TITLE = "Schedule a Consultation";

const ScheduleContext = createContext({ openSchedule: () => {} });
export const useSchedule = () => useContext(ScheduleContext);

export function ScheduleProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [bookingUrl, setBookingUrl] = useState(BOOKING_URL);
  const [title, setTitle] = useState(DEFAULT_TITLE);
  const openSchedule = useCallback((url, modalTitle) => {
    setBookingUrl(typeof url === "string" && url ? url : BOOKING_URL);
    setTitle(typeof modalTitle === "string" && modalTitle ? modalTitle : DEFAULT_TITLE);
    setOpen(true);
  }, []);
  const close = useCallback(() => setOpen(false), []);

  // Load GoHighLevel embed scripts once (handles iframe auto-resize for both
  // the legacy /widget/booking/ and the newer /widget/bookings/ widgets)
  useEffect(() => {
    if (!document.getElementById("ghl-booking-embed")) {
      const s = document.createElement("script");
      s.id = "ghl-booking-embed";
      s.src = "https://link.msgsndr.com/js/embed.js";
      s.async = true;
      document.body.appendChild(s);
    }
    if (!document.getElementById("ghl-form-embed")) {
      const s2 = document.createElement("script");
      s2.id = "ghl-form-embed";
      s2.src = "https://link.msgsndr.com/js/form_embed.js";
      s2.async = true;
      document.body.appendChild(s2);
    }
  }, []);

  // Lock page scroll + Escape to close
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  return (
    <ScheduleContext.Provider value={{ openSchedule }}>
      {children}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="schedule-modal"
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div
              className="absolute inset-0 bg-[#030303]/85 backdrop-blur-md"
              onClick={close}
              data-testid="schedule-overlay"
            />
            <motion.div
              className="relative w-full max-w-3xl bg-[#0a0a0a] border border-[#d4af37]/30 rounded-sm overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
              initial={{ opacity: 0, y: 30, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.98 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="flex items-center justify-between px-6 sm:px-8 py-5 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <CalendarDays size={18} className="text-[#d4af37]" />
                  <div className="flex flex-col leading-none">
                    <span className="serif text-lg text-[#f4f0ea]">{title}</span>
                    <span className="mono text-[9px] uppercase tracking-[0.3em] text-[#737373] mt-1">John Campbell Insurance</span>
                  </div>
                </div>
                <button
                  onClick={close}
                  data-testid="schedule-close"
                  aria-label="Close scheduler"
                  className="text-[#a3a3a3] hover:text-[#f4f0ea] transition-colors"
                >
                  <X size={22} />
                </button>
              </div>
              <div className="bg-[#0a0a0a]" style={{ height: "min(78vh, 760px)" }}>
                <iframe
                  src={bookingUrl}
                  title={title}
                  data-testid="schedule-iframe"
                  className="w-full h-full border-0"
                  scrolling="yes"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </ScheduleContext.Provider>
  );
}
